'use client';

import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { ForwardRefExoticComponent, SVGProps } from 'react';
import { useFocus } from '@/app/_hooks/useFocus';

export type InputSearchBarProps = {
  id?: string;
  value?: string;
  setValue: (value: string) => void;
  position: 'left' | 'right' | 'standalone';
  size?: 'sm' | 'lg';
  icon?: ForwardRefExoticComponent<Omit<SVGProps<SVGSVGElement>, 'ref'>>;
  placeholder?: string;
  onClick?: () => void;
  onClear?: () => void;
};

const InputSearchBar: React.FC<InputSearchBarProps> = ({
  position,
  size = 'sm',
  icon: IconComponent,
  placeholder,
  value,
  setValue,
  onClear,
  ...props
}) => {
  const iconClassName = clsx(value ? 'text-primary' : 'text-neutral-lighter', 'size-5', size === 'lg' && 'lg:size-7');

  const [inputRef, setFocus] = useFocus<HTMLInputElement>();

  const clearInput = () => {
    setValue('');
    setFocus();
    onClear?.();
  };

  return (
    <label
      className={clsx(
        'flex h-full w-full flex-1 cursor-text flex-row items-center gap-2 rounded border border-neutral-inactive bg-white px-2 py-2.5 transition-colors focus-within:border-primary-active lg:border-2 lg:border-transparent lg:bg-transparent',
        position === 'left' && 'lg:rounded-l-full lg:pl-4',
        position === 'right' && 'lg:group-last:rounded-r-full',
        position === 'standalone' && '!border !border-neutral-inactive',
      )}
    >
      {IconComponent && <IconComponent className={iconClassName} />}

      <input
        ref={inputRef}
        placeholder={placeholder}
        className={clsx(
          'h-full w-full bg-transparent text-base font-normal text-primary outline-none placeholder:text-neutral-lighter',
          size === 'lg' && 'lg:text-xl lg:font-medium',
        )}
        value={value}
        onChange={e => setValue(e.target.value)}
        {...props}
      />

      <button
        type="button"
        className={value ? 'visible' : 'pointer-events-none invisible'}
        onClick={clearInput}
        data-testid="clear-icon"
        aria-label="Reset campo testuale"
      >
        <XMarkIcon className={iconClassName} />
      </button>
    </label>
  );
};

export default InputSearchBar;
